/* eslint-disable @typescript-eslint/ban-ts-comment */
/* ignoring unicode regex cause no UTF-16 chars nor new Regex results in better handling of text instead of exec in legacy envs */

import { languagesEncode, DEFAULT_LANGUAGE, CO_COOKIE_LANG } from './constants';
import { CPCS_REGEX } from './cpcs';
import {
  ENDPOINT_URL_BASE_MODE,
  IEndpointConfig,
  IEnvironmentConfig,
} from './declarations';

/* eslint-disable require-unicode-regexp */
type callbackFN = (...args: any) => void;
const objectToString = Object.prototype.toString;
const hasOwn = Object.prototype.hasOwnProperty;

/**
 * @method memoize
 * @description memoize function
 * https://github.com/lodash/lodash/blob/master/memoize.js
 */

export const memoize = function (func, resolver?) {
  const memoized = function (this: any, ...args) {
    const key = resolver ? resolver.apply(this, args) : JSON.stringify(args);
    const cache = memoized.cache;
    if (!window['__TEST__'] && cache.has(key)) {
      return cache.get(key);
    }
    const result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new Map();
  return memoized;
};

/**
 * @method composeMyDomainWebviewURL
 * @description helper method that compose the correct format of the urls that need a myDomain integration
 */
export function composeMyDomainWebviewURL() {
  return (
    window.top!.location.origin +
    '/GPeticiones;WebLogicSession=' +
    (window as any).openFrontData.webLogicSession
  );
}

/**
 * @method queryStringToObject
 * @description helper method emit global events from the architecture packages
 * @param {string} value : event name
 */
export function queryStringToObject(value: string) {
  const removeWrongParameters = parameters => {
    const result = parameters
      .split('&')
      .filter(parameter => parameter.indexOf('=') > 0)
      .join('&');

    return result.length === 0 ? {} : result;
  };

  if (value.indexOf('?') > -1) {
    const raw = value.split('?');
    value = raw[0];

    const search = removeWrongParameters(raw[1]);
    if (typeof search !== 'string') {
      return search;
    }

    const hashes = search.slice(search.indexOf('?') + 1).split('&');

    return hashes.reduce((params, hash) => {
      const split = hash.indexOf('=');
      const key = hash.slice(0, split);
      const val = hash.slice(split + 1);
      return Object.assign(params, { [key]: decodeURIComponent(val) });
    }, {});
  } else {
    return {};
  }
}

/**
 * @method eventEmit
 * @description helper method emit global events from the architecture packages
 * @param {string} key : event name
 * @param {string} message : Message to be dispatched
 */
export const eventEmit = /*@__PURE__*/ function (
  key: string,
  message: string | any
): void {
  let event: CustomEvent<any> | null = null;
  /* istanbul ignore else */
  if (typeof window.CustomEvent === 'function') {
    event = new CustomEvent(key, {
      detail: message,
    });
  } else {
    event = document.createEvent('CustomEvent');
    event.initCustomEvent(key, false, false, message);
  }
  window.dispatchEvent(event);
};

/**
 * @method ENV_REGEX
 * @description This const holds Regex used to find environment and channel. the rationales are
 * focused on find first the env with biggest priority reducing lookup for production environments having has a max 2 loopkups
 * to find LO or CO.
 *
 * This rational replace a two step phase where to find `ca_dev_lo` would only take 4 lookups instead of actual
 * 7 but in the other hand `ca_pro_lo` will take one instead of two lookups to be found.
 *
 * ca_pro_lo -> https://loc[0-9].caixabank.es
 * ca_pro_mo -> https://loapp.caixabank.es/*
 * ca_pro_co -> https://pro0[0-9].tf7.lacaixa.es
 * ca_pro_co_international -> https://[a-z]{1-3}.tfm.lacaixa.es
 * ca_pro_co_corporative -> https://corp.pro.caixabank.com
 * ca_pre_lo -> https://pre.loc[0-9].caixabank.es
 * ca_pre_mo -> https://pre.lom.caixabank.es/*
 * ca_pre_co -> https://pre0[0-9].tf7.lacaixa.es
 * ca_pre_co_international -> https://pre.[a-z]{1-3}.tfm.lacaixa.es
 * ca_pre_co_corporative -> https://corp.pre.caixabank.com
 * ca_tst_lo -> https://int.loc[0-9].caixabank.es
 * ca_tst_mo -> https://int.lom.caixabank.es/*
 * ca_tst_co -> https://tst0[0-9].tf7.lacaixa.es
 * ca_pre_co_international -> https://tst.[a-z]{1-3}.tfm.lacaixa.es
 * ca_pre_co_corporative -> https://corp.tst.caixabank.com
 * ca_dev_lo -> localhost / 192.168.1.1 etc ...
 *
 */

const ENV_REGEX: { [key: string]: RegExp } = {
  ca_pro_lo: /(\/\/loc[0-9]{1,3})|(pro\.internet\.cloud)/,
  ca_pro_mo: /(\/\/loapp\.)/,
  ca_pro_co: /((\/\/pro[0-9]{1,3}\.tf7)|(pro\.ca\.))|(pro\.absis\.cloud)/,
  ca_pro_co_corporative: /\/\/corp\.pro\.caixabank/,
  ca_pro_co_international: /\/\/[a-z]{1,3}\.tfm\./,
  ca_pre_lo: /((\/\/pre\.loc[0-9]{1,3}))|(pre\.internet\.cloud)/,
  ca_pre_mo: /(\/\/pre\.lom\.)/,
  ca_pre_co: /((\/\/pre[0-9]{1,3}\.tf7)|(pre\.ca\.))|(pre\.absis\.cloud)/,
  ca_pre_co_corporative: /\/\/corp\.pre\.caixabank/,
  ca_pre_co_international: /\/\/pre\.[a-z]{1,3}\.tfm\./,
  ca_tst_lo:
    /((\/\/int\.loc[0-9]{1,3}))|(tst\.internet\.cloud)|(dev\.internet\.cloud)/,
  ca_tst_mo: /(\/\/int\.lom\.)/,
  ca_tst_co:
    /((\/\/tst[0-9]{1,3}\.tf7)|(tst\.ca\.))|(tst\.absis\.cloud)|(dev\.absis\.cloud)/,
  ca_tst_co_international: /\/\/tst\.[a-z]{1,3}\.tfm\./,
  ca_tst_co_corporative: /\/\/corp\.tst\.caixabank/,
  ca_dev_lo:
    /((((localhost)|(([2]([0-4][0-9]|[5][0-5])|[0-1]?[0-9]?[0-9])[.]){3}(([2]([0-4][0-9]|[5][0-5])|[0-1]?[0-9]?[0-9])))(:[0-9]{1,5})?[;]?)+)/,
  ...CPCS_REGEX,
};

/**
 * @method getEnvConfig
 * @description This method check environment and channel based on `url` params.check productive
 * environment's first for the sake of speed.
 */
export const getEnvConfig: (checkUrl: string) => IEnvironmentConfig | null =
  memoize(
    /*@__PURE__*/ function (checkUrl: string) {
      let env: IEnvironmentConfig | null = null;
      // TODO: CHANNELCONFIG -> Aquí deberíamos tener el canal neutro... pero como obtenemos el entorno sin url?
      Object.keys(ENV_REGEX).some(function (key) {
        // eslint-disable-next-line require-unicode-regexp
        return ENV_REGEX[key].test(checkUrl)
          ? // Be carefull changing this values , using substring for performance
            (env = {
              env: key.substring(0, 6),
              channel: key.substring(7, 9),
              subchannel: key.substring(7),
              shortEnv: key.substring(3, 6),
            })
          : null;
      });
      return env;
    }
  );

/**
 * @method debounce
 * @description Creates a debounce function that delays invoking func until after wait milliseconds have elapsed since the last time the debounce function was invoked.
 */
export const debounce = /*@__PURE__*/ function (
  func: callbackFN,
  timeout = 20
) {
  let timer: any | undefined;
  return (...args: any[]) => {
    const next = () => func(...args);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(next, timeout > 0 ? timeout : 300);
  };
};

/**
 * @method createIframe
 * @description method that creates a dynamic hidden iframe
 * @param {String} iframeURL : desired url to load into the iframe
 * @param {Function} ref : Callback function to invoke on the onload iframe event
 */
export const createIframe = /*@__PURE__*/ async function (iframeURL, ref) {
  await DOMContentReady();
  return new Promise(async function (resolve, reject) {
    let iframe: any = null;
    const removeIframe = () => {
      if (window.document.body.contains(iframe)) {
        window.document.body.removeChild(iframe);
        window.removeEventListener('message', ref, false);
      }
    };

    try {
      iframe = document.createElement('iframe');
      iframe.setAttribute('width', '0');
      iframe.setAttribute('height', '0');
      iframe.style.display = 'none';

      iframe.onload = event => ref(event, resolve, reject, removeIframe);
      iframe.onerror = error => {
        removeIframe();
        throw Error(error);
      };

      window.addEventListener('message', ref);
      window.document.body.appendChild(iframe);
      iframe.setAttribute('src', iframeURL);
    } catch (error) {
      removeIframe();
      return reject(error);
    }
  });
};

/**
 * @method uuid
 * @description generate unique id based on uuid v4
 */
export const uuid = /*@__PURE__*/ function () {
  // waiting for https://github.com/tc39/proposal-uuid
  // code from https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * @method secureData
 * @description recursive freezing of objects
 */
export function secureData(data: any): any {
  const propNames = Object.getOwnPropertyNames(data);
  propNames.forEach(name => {
    const prop = data[name];
    if (typeof prop === 'object' && prop !== null && !Object.isFrozen(prop))
      secureData(prop);
  });
  return Object.freeze(data);
}

/**
 * @method isPlainObject
 * @description recursive freezing of objects
 */

export function isPlainObject(possibleObj: unknown): boolean {
  return objectToString.call(possibleObj) === '[object Object]';
}

/**
 * @method isEmpty
 * @description Checks if value is an empty object or collection.
 */
export function isEmpty(obj: any): boolean {
  return (
    [Object, Array].includes((obj || {}).constructor) &&
    !Object.entries(obj || {}).length
  );
}

/**
 * @method hasOwnProp
 * @description cached version of hasOwnProperty
 */
export const hasOwnProp = /*@__PURE__*/ function (
  val: unknown,
  key: string | symbol
): key is keyof typeof val {
  return hasOwn.call(val, key);
};

/**
 * @method isObject
 * @description validates if it is an object
 */
export function isObject(val: unknown): boolean {
  return val !== null && typeof val === 'object';
}

/**
 * @method validateFields
 * @description validates fields in a object
 */
export function validateParams(params = {}, fields: any[], message?: string) {
  if (__DEV__) {
    return fields.some(field => {
      if (hasOwnProp(params, field) && !isEmpty(params[field])) {
      } else {
        const msg = message
          ? `${message} ${field}`
          : `Missing required parameter ${field}`;
        throw new Error(msg);
      }
    });
  }
}

/**
 * @method isLocal
 * @description helper method that returns if you are running in localhost or over ip address resolves as demo environment
 */
export const isLocal: () => boolean = memoize(
  /*@__PURE__*/ function () {
    {
      if (!(window && window.location && window.location.origin)) return false;
      const pattern =
        /((((localhost)|(([2]([0-4][0-9]|[5][0-5])|[0-1]?[0-9]?[0-9])[.]){3}(([2]([0-4][0-9]|[5][0-5])|[0-1]?[0-9]?[0-9])))(:[0-9]{1,5})?[;]?)+)/g;
      return pattern.test(window.location.origin);
    }
  }
);

/**
 * @method isIE
 * @description check if is internet explorer
 */
export const isIE: () => boolean = memoize(
  /*@__PURE__*/ function () {
    return (
      typeof navigator !== 'undefined' &&
      navigator.userAgent &&
      (navigator.userAgent.indexOf('MSIE') !== -1 ||
        navigator.appVersion.indexOf('Trident/') > -1)
    );
  }
);

/**
 * @method decodeURLParams
 * @description obtain url parameters from a url like #acess_token=myvalue&state=urlstate and retrieve as object
 */
export const decodeURLParams = /*@__PURE__*/ (
  search: string
): { [key: string]: string } => {
  const hashes = search.slice(search.indexOf('?') + 1).split('&');
  return hashes.reduce((params, hash) => {
    const split = hash.indexOf('=');
    const key = hash.slice(0, split);
    const val = hash.slice(split + 1);
    return Object.assign(params, { [key]: decodeURIComponent(val) });
  }, {});
};

/**
 * @method buildResolutorParameters
 * @description parse pea and params for navigate wiht it
 * @param {String} pea : entry point of app
 * @param {Object} params : dynamic navigation parameters
 */
export function buildResolutorParameters(
  pea: string,
  params?: Record<string, unknown>
) {
  return JSON.stringify({
    PEA: pea,
    ...(params ? params : {}),
  });
}

/**
 * @method navigateToPEA
 * @description navigation method with PEA + Params
 * @param {String} pea : entry point of app
 * @param {Object} target : it's a ref to the obj which the event was dispatched
 * @param {Object} params : dynamic navigation parameters
 */
export function navigateToPEA(pea, params, target) {
  const attributesList = {
    type: 'resolutor',
    ['resolutor-type']: 'PEA',
    ['resolutor-parameters']: buildResolutorParameters(pea, params),
  };

  triggerRouterNavigation(target, attributesList);
}

/**
 * @method triggerRouterNavigation
 * @description method to add a tag a in the DOM and navigate with params
 * @param {Object} target : it's a ref to the obj which the event was dispatched
 * @param {Object} attributesList : pass dynamic parameters
 */
export function triggerRouterNavigation(
  target: HTMLElement,
  attributesList: Record<string, string>
) {
  const anchor = document.createElement('a');
  anchor.href = '/';

  // set attributes
  Object.keys(attributesList).forEach(attribute => {
    anchor.setAttribute(attribute, attributesList[attribute]);
  });

  target && target.appendChild(anchor);

  if (target && anchor) {
    anchor.click();
    target.removeChild(anchor);
  }
}

/**
 * @method isWebView
 * @description method that returns if app is running in a webview
 */
export function isWebView(): boolean {
  return (<any>window).Capacitor && (<any>window).Capacitor.isNative;
}

/**
 * @method getBcpFromIso
 * @description method that returns the correct BCP for language entry (iso or BCP)
 */
export function getBcpFromLanguageString(languageString: string): string {
  const language = Object.values(languagesEncode).find(
    language =>
      language.iso.toLowerCase() === languageString.toLowerCase() ||
      language.bcp.toLowerCase() === languageString.toLowerCase()
  );

  return (language && language.bcp) || DEFAULT_LANGUAGE;
}

export function isWebViewIniOS(): boolean {
  // https://developers.whatismybrowser.com/useragents/explore/operating_system_name/ios/
  const standalone = (window.navigator as any).standalone;
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isiOS = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent);
  return (
    (isiOS && !standalone) ||
    ((window as any).webkit && (window as any).webkit.messageHandlers)
  );
}

export function isWebViewInAndroid(): boolean {
  // https://developer.chrome.com/docs/multidevice/user-agent/
  return /Version\/\d+.*\/\d+.0.0.0 Mobile|; ?wv/i.test(
    window.navigator.userAgent
  );
}

export function isLoadInWebview(): boolean {
  return isWebViewIniOS() || isWebViewInAndroid();
}

export function getSessionLanguageFromLO(): string {
  if (isWebView()) {
    return getBcpFromLanguageString(window.openFrontData.language);
  } else {
    const language =
      window.top &&
      (window.top.flagIdioma ||
        (window.top.flags && window.top.flags.flagIdioma));
    return (
      (language &&
        languagesEncode[language] &&
        languagesEncode[language].bcp) ||
      DEFAULT_LANGUAGE
    );
  }
}

export function getSessionLanguageFromCO(): string {
  const sessionLanguageId = getCookie(CO_COOKIE_LANG);

  return sessionLanguageId
    ? getBcpLanguageById(sessionLanguageId)
    : DEFAULT_LANGUAGE;
}

export function getSessionLanguage(channel?: string): string {
  return channel && isCOChannel(channel)
    ? getSessionLanguageFromCO()
    : getSessionLanguageFromLO();
}

/**
 * @description get bcp language code by the id used in now or co
 * @param {string} sessionLanguageId language id stored by the session
 * @returns {string} bcp language id or default language if not find it
 */
export function getBcpLanguageById(sessionLanguageId: string): string {
  const language = languagesEncode[sessionLanguageId];
  return language ? language.bcp : DEFAULT_LANGUAGE;
}

/**
 * @description get content cookie with key param
 * @param {string} key identifier of the cookie data
 * @returns {string} content of cookie with key param, undefined if doesn't exist
 */
export function getCookie(key: string): string | undefined {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${key}=`);
  if (parts && parts.length === 2) {
    const cookie = parts.pop();
    return cookie && cookie.split(';').shift();
  }
}

export function redirectToLogin(
  loginUrl: string | undefined,
  redirectURI: string
): void {
  if (loginUrl) {
    window.location.href =
      window.location.origin +
      loginUrl +
      '?loginUrl=' +
      encodeURIComponent(redirectURI);
  }
}

export function downloadBlob(blob: Blob, fileName?: string): void {
  if (window.navigator['msSaveBlob']) {
    window.navigator['msSaveBlob'](blob, fileName);
  } else {
    const url = URL.createObjectURL(blob);
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.setAttribute('download', fileName ? fileName : '');

    window.document.body.appendChild(anchor);
    anchor.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );
    window.document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }
}

export function resolveError(error: unknown): Error {
  return error instanceof Error ? error : new Error('' + error);
}

export function isCOChannel(channel: string): boolean {
  return channel.toLocaleLowerCase() === 'co';
}

export function isLOChannel(channel: string): boolean {
  return channel.toLocaleLowerCase() === 'lo';
}

export async function callHttpRequest(
  request: {
    url: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    headers?: Record<string, any>;
    body?: Record<string, any>;
  },
  options: { responseAsJson: boolean } = {
    responseAsJson: false,
  }
): Promise<any> {
  const response = await window.fetch(request.url, {
    method: request.method || 'GET',
    headers: request.headers,
    body: request.body ? JSON.stringify(request.body) : undefined,
  });
  return await (options.responseAsJson ? response.json() : response);
}

export const getFullEndpoint = memoize(function (
  endpoint: IEndpointConfig
): string {
  let baseEndpoint = window.top.location.origin;
  if (endpoint.urlBase === ENDPOINT_URL_BASE_MODE.MY_DOMAIN) {
    baseEndpoint = isWebView()
      ? composeMyDomainWebviewURL()
      : window.top.encodedURL;
  } else if (endpoint.urlBase) {
    baseEndpoint = endpoint.urlBase;
  }

  return baseEndpoint + endpoint.path;
});

export const DOMContentReady = (): Promise<void> => {
  return new Promise<void>(resolve => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resolve());
    } else {
      resolve();
    }
  });
};
